/**
 * WCAG contrast and readability suggestions for chart custom colors.
 * WCAG 2.1 Non-text Contrast (graphical objects): ratio >= 3:1 passes.
 * suggestReadable moves only lightness until it passes.
 * Zero hex literals in source code.
 */

export interface ColorContrastResult {
  passes: boolean
  ratio: number
}

interface RgbColor {
  r: number
  g: number
  b: number
}

interface HslColor {
  h: number
  s: number
  l: number
}

function parseHex(hex: string): RgbColor {
  const clean = hex.replace(/^#/, '').trim()
  if (clean.length === 3) {
    const r = parseInt(clean[0] + clean[0], 16) || 0
    const g = parseInt(clean[1] + clean[1], 16) || 0
    const b = parseInt(clean[2] + clean[2], 16) || 0
    return { r, g, b }
  }
  const r = parseInt(clean.slice(0, 2), 16) || 0
  const g = parseInt(clean.slice(2, 4), 16) || 0
  const b = parseInt(clean.slice(4, 6), 16) || 0
  return { r, g, b }
}

function getChannelLuminance(val: number): number {
  const s = val / 255
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
}

function getRelativeLuminance(rgb: RgbColor): number {
  return 0.2126 * getChannelLuminance(rgb.r) + 0.7152 * getChannelLuminance(rgb.g) + 0.0722 * getChannelLuminance(rgb.b)
}

function getContrastRatio(l1: number, l2: number): number {
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

function rgbToHsl(rgb: RgbColor): HslColor {
  const r = rgb.r / 255
  const g = rgb.g / 255
  const b = rgb.b / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  return { h, s, l }
}

function hue2rgb(p: number, q: number, t: number): number {
  let tt = t
  if (tt < 0) tt += 1
  if (tt > 1) tt -= 1
  if (tt < 1 / 6) return p + (q - p) * 6 * tt
  if (tt < 1 / 2) return q
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
  return p
}

function hslToRgb(hsl: HslColor): RgbColor {
  const { h, s, l } = hsl
  if (s === 0) {
    const v = Math.round(l * 255)
    return { r: v, g: v, b: v }
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  return {
    r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  }
}

function formatHex(rgb: RgbColor): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)))
  const rHex = clamp(rgb.r).toString(16).padStart(2, '0')
  const gHex = clamp(rgb.g).toString(16).padStart(2, '0')
  const bHex = clamp(rgb.b).toString(16).padStart(2, '0')
  return '#' + rHex + gHex + bHex
}

/**
 * Checks contrast of hex against surface color.
 * Returns { passes: boolean, ratio: number } where ratio >= 3.0 passes.
 */
export function checkChartColor(hex: string, surface: string): ColorContrastResult {
  const rgb1 = parseHex(hex)
  const rgb2 = parseHex(surface)
  const l1 = getRelativeLuminance(rgb1)
  const l2 = getRelativeLuminance(rgb2)
  const ratio = getContrastRatio(l1, l2)
  return {
    passes: ratio >= 3.0,
    ratio: Math.round(ratio * 100) / 100,
  }
}

/**
 * Suggests a readable color by moving only lightness until contrast >= 3.0 against surface.
 */
export function suggestReadable(hex: string, surface: string): string {
  if (checkChartColor(hex, surface).passes) {
    return hex
  }

  const rgb = parseHex(hex)
  const surfaceRgb = parseHex(surface)
  const surfaceLum = getRelativeLuminance(surfaceRgb)
  const hsl = rgbToHsl(rgb)

  // If surface is light (luminance > 0.5), decrease lightness toward 0.
  // If surface is dark (luminance <= 0.5), increase lightness toward 1.
  const step = surfaceLum > 0.5 ? -0.02 : 0.02
  let currentL = hsl.l

  for (let i = 0; i < 50; i++) {
    currentL += step
    if (currentL < 0) {
      currentL = 0
      break
    }
    if (currentL > 1) {
      currentL = 1
      break
    }
    const candidateRgb = hslToRgb({ h: hsl.h, s: hsl.s, l: currentL })
    const candidateHex = formatHex(candidateRgb)
    if (checkChartColor(candidateHex, surface).passes) {
      return candidateHex
    }
  }

  const finalRgb = hslToRgb({ h: hsl.h, s: hsl.s, l: currentL })
  return formatHex(finalRgb)
}
